import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';

import { useMasterclass } from '@/contexts/MasterclassContext';
import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { OnboardingCarousel } from '@/modules/onboarding';
import { VideoModal } from '@/modules/video-modal';

import { CaseCard } from './CaseCard';
import { CertificationModal } from './CertificationModal';
import defaultConfig from '../config/selector-config.json';
import { useCaseDetails } from '../hooks/useCaseDetails';
import { useCaseSelector } from '../hooks/useCaseSelector';
import { CaseSelectorProps, CaseSelectorConfig } from '../types';

export const CaseSelector = ({ config: configOverride }: CaseSelectorProps) => {
  // Merge default config with overrides
  const config = {
    ...defaultConfig,
    ...configOverride,
    // NOTE: It is ok to cast here because we don't have a choice to transform the json transition type
    // to the correct type
    display: {
      ...(defaultConfig.display as CaseSelectorConfig['display']),
      ...configOverride?.display,
    },
    header: { ...defaultConfig.header, ...configOverride?.header },
    caseCard: { ...defaultConfig.caseCard, ...configOverride?.caseCard },
    navigation: { ...defaultConfig.navigation, ...configOverride?.navigation },
    modals: { ...defaultConfig.modals, ...configOverride?.modals },
    colors: { ...defaultConfig.colors, ...configOverride?.colors },
  } satisfies CaseSelectorConfig;

  const { cases } = useCasesConfig();
  const {
    userId,
    pendingCase,
    certificationCase,
    setCertificationCase,
    showIntroVideo,
    showConfirmationVideo,
    isLoading,
    caseProgress,
    caseProgressLoading,
    caseIntroVideoUrl,
    handleIntroClose,
    handleCaseSelect,
    handleConfirmationClose,
    handleConfirmationStart,
    handleSignOut,
  } = useCaseSelector(config);

  const { isDemo, resetCaseProgress } = useMasterclass();

  const selectedCaseItem = cases.find((c) => c.id === pendingCase);
  const { details: caseDetails, loading: caseDetailsLoading } = useCaseDetails(
    selectedCaseItem?.configUrl || null
  );

  // NOTE: if we are in demo mode, we want to reset the case progress on mount
  useEffect(() => {
    if (!isDemo) return;
    console.log('Demo mode detected - resetting case progress');
    cases.forEach((caseItem) => {
      console.log(`Resetting progress for case: ${caseItem.id}`);
      void resetCaseProgress(caseItem.id);
    });
  }, [cases, isDemo, resetCaseProgress]);

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-8">
        <div className="mx-auto max-w-6xl space-y-8">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-card-foreground">{config.header.title}</h1>
            <p className="text-muted-foreground">{config.header.subtitle}</p>
          </div>

          {/* Cases Grid */}
          <div className="mx-auto grid max-w-md grid-cols-1 gap-6">
            {cases.map((caseItem) => {
              const progress = caseProgress[caseItem.id];
              const isCompleted = progress?.completed;
              const isStarted = progress?.visitedSlideIds && progress.visitedSlideIds.length > 0;
              const progressPercentage = isCompleted
                ? 100
                : ((progress?.visitedSlideIds.length || 0) / 13) * 100;

              return (
                <CaseCard
                  key={caseItem.id}
                  caseId={caseItem.id}
                  title={caseItem.title}
                  configUrl={caseItem.configUrl}
                  assetBaseUrl={caseItem.assetsBaseUrl}
                  isCompleted={isCompleted || false}
                  isStarted={isStarted || false}
                  progress={progressPercentage}
                  userId={userId}
                  onSelect={(caseId) => void handleCaseSelect(caseId)}
                  onStartCertification={setCertificationCase}
                  config={config}
                  isLoadingOverride={caseProgressLoading}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Intro Onboarding Carousel */}
      {config.modals.showIntroOnFirstVisit && (
        <OnboardingCarousel isOpen={showIntroVideo} onClose={() => void handleIntroClose()} />
      )}

      {/* Confirmation Video Modal */}
      {config.modals.showConfirmationBeforeStart &&
        config.navigation.confirmationVideo.enabled &&
        caseDetails && (
          <VideoModal
            isOpen={showConfirmationVideo}
            onClose={handleConfirmationClose}
            onStart={() => void handleConfirmationStart()}
            title={`${config.navigation.confirmationVideo.modalTitle} "${caseDetails.displayName}"`}
            message={config.navigation.confirmationVideo.modalMessage}
            buttonText={config.navigation.confirmationVideo.buttonText}
            videoUrl={caseIntroVideoUrl || config.navigation.confirmationVideo.videoUrl}
          />
        )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-card p-8 shadow-2xl">
            <Loader2 className="h-12 w-12 animate-spin text-accent" />
            <p className="text-lg font-medium text-card-foreground">
              {config.navigation.loadingText}
            </p>
          </div>
        </div>
      )}

      {/* Certification Test or Review Modal */}
      {certificationCase && userId && (
        <CertificationModal
          caseId={certificationCase}
          userId={userId}
          onClose={() => setCertificationCase(null)}
        />
      )}
    </div>
  );
};
